import React, { useState } from 'react';
import { useNotifications } from '../../hooks/useNotifications';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import type { NotificationSettings } from '../../types/notification';
import CommonPageHeader from '../../components/common/CommonPageHeader';
import LoadingScreen from '../../components/common/LoadingScreen';
import { Button } from '../../components/common/Button';
import { Switch } from '../../components/common/Switch';
import { 
  BellIcon, 
  EnvelopeIcon, 
  ComputerDesktopIcon,
  SpeakerWaveIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import PushSettingsCard from '../../components/settings/PushSettingsCard';

interface NotificationSettingsPageProps {
  embedded?: boolean;
}

const NotificationSettingsPage: React.FC<NotificationSettingsPageProps> = ({ embedded = false }) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { settings, loading, updateSettings, createNotification } = useNotifications();
  const [saving, setSaving] = useState(false);
  const [localSettings, setLocalSettings] = useState<NotificationSettings | null>(settings);
  const [testingSample, setTestingSample] = useState(false);

  // Frissítjük a local settings-et amikor a settings változik
  React.useEffect(() => {
    if (settings) {
      setLocalSettings(settings);
    }
  }, [settings]);

  const handleEmailNotificationChange = (key: keyof NotificationSettings['emailNotifications'], value: boolean) => {
    if (!localSettings) return;
    
    setLocalSettings({
      ...localSettings,
      emailNotifications: {
        ...localSettings.emailNotifications,
        [key]: value
      }
    });
  };

  const handleInAppNotificationChange = (key: keyof NotificationSettings['inAppNotifications'], value: boolean) => {
    if (!localSettings) return;
    
    setLocalSettings({
      ...localSettings,
      inAppNotifications: {
        ...localSettings.inAppNotifications,
        [key]: value
      }
    });
  };

  const handleQuietHoursChange = (field: 'enabled' | 'startTime' | 'endTime', value: boolean | string) => {
    if (!localSettings) return;
    
    setLocalSettings({
      ...localSettings,
      quietHours: {
        ...localSettings.quietHours,
        [field]: value
      }
    });
  };

  const handleSave = async () => {
    if (!localSettings) return;
    
    setSaving(true);
    try {
      await updateSettings(localSettings);
      toast.success(t('settings.notifications.saveSuccess'));
    } catch (error) {
      console.error('Error saving notification settings:', error);
      toast.error(t('settings.notifications.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleTestNotification = async () => {
    if (!user?.id || !user?.organizationId) return;
    
    setTestingSample(true);
    try {
      await createNotification({
        type: 'system',
        title: '📢 Teszt értesítés',
        message: 'Ez egy teszt értesítés. Ha látod, akkor minden rendben működik! 🎉',
        priority: 'medium',
        userId: user.id,
        organizationId: user.organizationId,
        createdBy: user.id,
        createdByName: user.displayName,
        actionUrl: '/settings/notifications'
      });
      toast.success('Teszt értesítés elküldve! Nézd meg a harang ikont.');
    } catch (error) {
      console.error('Error sending test notification:', error);
      toast.error('Hiba a teszt értesítés küldésekor');
    } finally {
      setTestingSample(false);
    }
  };

  if (loading) {
    return embedded ? (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    ) : (
      <LoadingScreen />
    );
  }

  if (!localSettings) {
    return (
      <div className={embedded ? "text-center py-8" : "space-y-6 pb-20 sm:pb-6"}>
        <div className="text-center">
          <p className="text-gray-500 dark:text-gray-400">{t('settings.notifications.loadError')}</p>
        </div>
      </div>
    );
  }

  const content = (
    <div className="space-y-6">
      {/* Első sor - 3 oszlop desktop-on */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
      {/* Email értesítések */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
              <EnvelopeIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t('settings.notifications.emailNotifications')}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('settings.notifications.emailDescription')}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('notifications.types.task_assigned')}
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('settings.notifications.taskAssignedDesc')}
                </p>
              </div>
              <Switch
                checked={localSettings.emailNotifications.taskAssigned}
                onChange={(value: boolean) => handleEmailNotificationChange('taskAssigned', value)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('notifications.types.task_completed')}
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('settings.notifications.taskCompletedDesc')}
                </p>
              </div>
              <Switch
                checked={localSettings.emailNotifications.taskCompleted}
                onChange={(value: boolean) => handleEmailNotificationChange('taskCompleted', value)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('notifications.types.task_overdue')}
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('settings.notifications.taskOverdueDesc')}
                </p>
              </div>
              <Switch
                checked={localSettings.emailNotifications.taskOverdue}
                onChange={(value: boolean) => handleEmailNotificationChange('taskOverdue', value)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('notifications.types.project_update')}
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('settings.notifications.projectUpdateDesc')}
                </p>
              </div>
              <Switch
                checked={localSettings.emailNotifications.projectUpdates}
                onChange={(value: boolean) => handleEmailNotificationChange('projectUpdates', value)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('notifications.types.deadline_reminder')}
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('settings.notifications.deadlineReminderDesc')}
                </p>
              </div>
              <Switch
                checked={localSettings.emailNotifications.deadlineReminders}
                onChange={(value: boolean) => handleEmailNotificationChange('deadlineReminders', value)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('notifications.types.system')}
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('settings.notifications.systemDesc')}
                </p>
              </div>
              <Switch
                checked={localSettings.emailNotifications.systemNotifications}
                onChange={(value: boolean) => handleEmailNotificationChange('systemNotifications', value)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Ügyfél frissítések
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Új ügyfél létrehozása, státusz változás
                </p>
              </div>
              <Switch
                checked={localSettings.emailNotifications.customerUpdates}
                onChange={(value: boolean) => handleEmailNotificationChange('customerUpdates', value)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Ajánlat frissítések
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Új ajánlat, státusz változás (elfogadva/elutasítva)
                </p>
              </div>
              <Switch
                checked={localSettings.emailNotifications.quotationUpdates}
                onChange={(value: boolean) => handleEmailNotificationChange('quotationUpdates', value)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Interakciók
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Telefonhívás, email, SMS rögzítése
                </p>
              </div>
              <Switch
                checked={localSettings.emailNotifications.dealUpdates}
                onChange={(value: boolean) => handleEmailNotificationChange('dealUpdates', value)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Szerződés frissítések
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Új szerződés, státusz változás (érvényes/lejárt)
                </p>
              </div>
              <Switch
                checked={localSettings.emailNotifications.contractUpdates}
                onChange={(value: boolean) => handleEmailNotificationChange('contractUpdates', value)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Szervezet frissítések
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Szervezet létrehozása, módosítása, törlése
                </p>
              </div>
              <Switch
                checked={localSettings.emailNotifications.organizationUpdates}
                onChange={(value: boolean) => handleEmailNotificationChange('organizationUpdates', value)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Felhasználó frissítések
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Felhasználó hozzáadása/eltávolítása, jogosultságok
                </p>
              </div>
              <Switch
                checked={localSettings.emailNotifications.userUpdates}
                onChange={(value: boolean) => handleEmailNotificationChange('userUpdates', value)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Űrlap kitöltések
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Új űrlap beküldése, válaszok
                </p>
              </div>
              <Switch
                checked={localSettings.emailNotifications.formSubmissions}
                onChange={(value: boolean) => handleEmailNotificationChange('formSubmissions', value)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Időpont foglalások
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Új időpont, módosítás, törlés
                </p>
              </div>
              <Switch
                checked={localSettings.emailNotifications.appointmentUpdates}
                onChange={(value: boolean) => handleEmailNotificationChange('appointmentUpdates', value)}
              />
            </div>

          </div>
        </div>

        {/* Alkalmazáson belüli értesítések */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg">
              <BellIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t('settings.notifications.inAppNotifications')}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('settings.notifications.inAppDescription')}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('notifications.types.task_assigned')}
                </label>
              </div>
              <Switch
                checked={localSettings.inAppNotifications.taskAssigned}
                onChange={(value: boolean) => handleInAppNotificationChange('taskAssigned', value)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('notifications.types.task_completed')}
                </label>
              </div>
              <Switch
                checked={localSettings.inAppNotifications.taskCompleted}
                onChange={(value: boolean) => handleInAppNotificationChange('taskCompleted', value)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('notifications.types.task_overdue')}
                </label>
              </div>
              <Switch
                checked={localSettings.inAppNotifications.taskOverdue}
                onChange={(value: boolean) => handleInAppNotificationChange('taskOverdue', value)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('notifications.types.project_update')}
                </label>
              </div>
              <Switch
                checked={localSettings.inAppNotifications.projectUpdates}
                onChange={(value: boolean) => handleInAppNotificationChange('projectUpdates', value)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('notifications.types.deadline_reminder')}
                </label>
              </div>
              <Switch
                checked={localSettings.inAppNotifications.deadlineReminders}
                onChange={(value: boolean) => handleInAppNotificationChange('deadlineReminders', value)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('notifications.types.system')}
                </label>
              </div>
              <Switch
                checked={localSettings.inAppNotifications.systemNotifications}
                onChange={(value: boolean) => handleInAppNotificationChange('systemNotifications', value)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('notifications.types.mention')}
                </label>
              </div>
              <Switch
                checked={localSettings.inAppNotifications.mentions}
                onChange={(value: boolean) => handleInAppNotificationChange('mentions', value)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Ügyfél frissítések
                </label>
              </div>
              <Switch
                checked={localSettings.inAppNotifications.customerUpdates}
                onChange={(value: boolean) => handleInAppNotificationChange('customerUpdates', value)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Ajánlat frissítések
                </label>
              </div>
              <Switch
                checked={localSettings.inAppNotifications.quotationUpdates}
                onChange={(value: boolean) => handleInAppNotificationChange('quotationUpdates', value)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Interakciók
                </label>
              </div>
              <Switch
                checked={localSettings.inAppNotifications.dealUpdates}
                onChange={(value: boolean) => handleInAppNotificationChange('dealUpdates', value)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Szerződés frissítések
                </label>
              </div>
              <Switch
                checked={localSettings.inAppNotifications.contractUpdates}
                onChange={(value: boolean) => handleInAppNotificationChange('contractUpdates', value)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Szervezet frissítések
                </label>
              </div>
              <Switch
                checked={localSettings.inAppNotifications.organizationUpdates}
                onChange={(value: boolean) => handleInAppNotificationChange('organizationUpdates', value)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Felhasználó frissítések
                </label>
              </div>
              <Switch
                checked={localSettings.inAppNotifications.userUpdates}
                onChange={(value: boolean) => handleInAppNotificationChange('userUpdates', value)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Űrlap kitöltések
                </label>
              </div>
              <Switch
                checked={localSettings.inAppNotifications.formSubmissions}
                onChange={(value: boolean) => handleInAppNotificationChange('formSubmissions', value)}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Időpont foglalások
                </label>
              </div>
              <Switch
                checked={localSettings.inAppNotifications.appointmentUpdates}
                onChange={(value: boolean) => handleInAppNotificationChange('appointmentUpdates', value)}
              />
            </div>
          </div>
        </div>

        {/* Egyéb beállítások */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
              <ComputerDesktopIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t('settings.notifications.otherSettings')}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('settings.notifications.otherDescription')}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <SpeakerWaveIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('settings.notifications.soundEnabled')}
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t('settings.notifications.soundEnabledDesc')}
                  </p>
                </div>
              </div>
              <Switch
                checked={localSettings.soundEnabled}
                onChange={(value: boolean) => setLocalSettings({ ...localSettings, soundEnabled: value })}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <ComputerDesktopIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('settings.notifications.desktopNotifications')}
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {t('settings.notifications.desktopNotificationsDesc')}
                  </p>
                </div>
              </div>
              <Switch
                checked={localSettings.desktopNotifications}
                onChange={async (value: boolean) => {
                  setLocalSettings({ ...localSettings, desktopNotifications: value });
                  if (value && typeof window !== 'undefined' && 'Notification' in window && Notification.permission !== 'granted') {
                    try {
                      const perm = await Notification.requestPermission();
                      if (perm === 'granted') { new Notification('YouCRM', { body: 'Az asztali értesítések bekapcsolva.', icon: '/favicon.svg' }); }
                      else toast.error('A böngésző nem engedélyezte az értesítéseket – a címsor melletti lakat ikonnál engedélyezhető.');
                    } catch { /* ignore */ }
                  }
                }}
              />
            </div>

            <div className="pt-1"><PushSettingsCard /></div>

            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-1 bg-red-100 dark:bg-red-900/20 rounded">
                    <svg className="h-5 w-5 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('settings.notifications.debugMode')}
                    </label>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t('settings.notifications.debugModeDesc')}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={localSettings.debugMode}
                  onChange={(value: boolean) => setLocalSettings({ ...localSettings, debugMode: value })}
                />
              </div>
            </div>
            </div>
          </div>
        </div>

      {/* Második sor - Csendes órák */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 bg-orange-100 dark:bg-orange-900/20 rounded-lg">
              <ClockIcon className="h-6 w-6 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t('settings.notifications.quietHours')}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('settings.notifications.quietHoursDesc')}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('settings.notifications.enableQuietHours')}
                </label>
              </div>
              <Switch
                checked={localSettings.quietHours.enabled}
                onChange={(value: boolean) => handleQuietHoursChange('enabled', value)}
              />
            </div>

            {localSettings.quietHours.enabled && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('settings.notifications.startTime')}
                  </label>
                  <input
                    type="time"
                    value={localSettings.quietHours.startTime}
                    onChange={(e) => handleQuietHoursChange('startTime', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('settings.notifications.endTime')}
                  </label>
                  <input
                    type="time"
                    value={localSettings.quietHours.endTime}
                    onChange={(e) => handleQuietHoursChange('endTime', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Spacer oszlop - üres hely a többi kártya miatt */}
        <div className="hidden xl:block"></div>
        <div className="hidden xl:block"></div>
        </div>

        {/* Mentés és Teszt gombok */}
        <div className="flex justify-end space-x-3">
          <Button
            onClick={handleTestNotification}
            disabled={testingSample}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            {testingSample ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Küldés...
              </>
            ) : (
              <>
                <BellIcon className="h-4 w-4 mr-2" />
                Teszt értesítés
              </>
            )}
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {t('common.saving')}
              </>
            ) : (
              <>
                <CheckCircleIcon className="h-4 w-4 mr-2" />
                {t('common.save')}
              </>
            )}
          </Button>
        </div>
    </div>
  );

  if (embedded) {
    return content;
  }

  return (
    <div className="space-y-6 pb-20 sm:pb-6">
      <CommonPageHeader
        title={t('settings.notifications.title')}
        subtitle={t('settings.notifications.subtitle') || 'Az értesítési beállítások testreszabása és kezelése'}
        icon={BellIcon}
      />
      {content}
    </div>
  );
};

export default NotificationSettingsPage; 